import { useMemo } from 'react';
import { Autocomplete, type Option } from '@finn-auto/design-system/atoms/autocomplete';
import { usePostingTitles } from './usePostingTitles';
import type { InterviewLink } from '../../services/interviewLinksApi';

export interface RoleOption {
  postingId: string;
  jobTitle: string;
  /** False when every link for this role is closed. */
  hasActive: boolean;
}

/**
 * Distinct roles that already have at least one interview link.
 *
 * Keyed by posting id, because that is what the overview groups on — two links
 * only appear under one role when they share it.
 *
 * Roles whose links are ALL closed are kept, not filtered out: adding a stage to
 * a role you are picking back up is a real thing to do. They are flagged instead,
 * because the overview hides closed links by default — so without a marker this
 * list offers roles that appear nowhere on the previous screen, which reads as a
 * bug rather than a feature.
 *
 * An option carries the ROLE and nothing else. It used to list the role's existing
 * stages after the title, which made the picked value read "Senior Product Manager
 * Growth — Personal Interview" — i.e. exactly like a role whose name contains a
 * stage, in a form whose next field is the stage. What is being chosen here is the
 * posting; the stages already have their own screen.
 */
export function rolesFromLinks(links: InterviewLink[]): RoleOption[] {
  const byPosting = new Map<string, RoleOption>();
  for (const link of links) {
    if (!link.postingId) continue;
    const found = byPosting.get(link.postingId);
    if (found) {
      // A single live link is enough to call the role live, and the title of an
      // active link beats one carried over from a closed one.
      if (link.active && !found.hasActive) {
        found.hasActive = true;
        found.jobTitle = link.jobTitle || link.name;
      }
      continue;
    }
    byPosting.set(link.postingId, {
      postingId: link.postingId,
      jobTitle: link.jobTitle || link.name,
      hasActive: Boolean(link.active),
    });
  }
  // Live roles first: those are the ones the overview is showing right now.
  return [...byPosting.values()].sort(
    (a, b) => Number(b.hasActive) - Number(a.hasActive) || a.jobTitle.localeCompare(b.jobTitle)
  );
}

export interface LabelledRole extends RoleOption {
  /** The role's name as the list shows it, without the "(all links closed)" note. */
  name: string;
  label: string;
}

/**
 * What a role is called in the list: the title it has under "Lever posting", because that is the
 * name the person just searched. The link's own "Role title (shown to the candidate)" — often a
 * stage-flavoured text like "First Interview | FINN" — is never part of the name; it only stands
 * in when Lever has no title (still loading, Lever down, posting gone).
 */
export function labelRoles(roles: RoleOption[], postingTitles: Map<string, string>): LabelledRole[] {
  const labelled = roles.map((r) => {
    const shown = postingTitles.get(r.postingId.toLowerCase()) || r.jobTitle;
    return { ...r, name: shown, label: `${shown}${r.hasActive ? '' : ' (all links closed)'}` };
  });
  // The list keys its options on the label, so two roles with the same label need telling apart.
  const count = new Map<string, number>();
  for (const r of labelled) count.set(r.label, (count.get(r.label) ?? 0) + 1);
  return labelled
    .map((r) => ((count.get(r.label) ?? 0) > 1 ? { ...r, label: `${r.label} · ${r.postingId.slice(0, 8)}` } : r))
    .sort((a, b) => Number(b.hasActive) - Number(a.hasActive) || a.label.localeCompare(b.label));
}

/**
 * Attach this link to a role that already exists.
 *
 * A role runs several interviews and each one is its own link, so the common case
 * is adding a stage to a role already set up. Searching the posting again by hand
 * for every stage invites a different title each time, and the overview groups on
 * the posting — so one mistyped lookup scatters a role across several headings.
 *
 * Picking here fills the posting AND the title together, which is the only way
 * the stages reliably end up side by side. The list is searchable and names each
 * role by its Lever posting title (see `labelRoles`).
 */
export function ExistingRolePicker({
  links,
  postingId,
  onPick,
}: {
  links: InterviewLink[];
  postingId: string;
  onPick: (role: RoleOption | null) => void;
}) {
  const baseRoles = useMemo(() => rolesFromLinks(links), [links]);
  const postingTitles = usePostingTitles(links);
  const roles = useMemo(() => labelRoles(baseRoles, postingTitles), [baseRoles, postingTitles]);
  const options = useMemo<Option[]>(() => roles.map((r) => ({ value: r.postingId, label: r.label })), [roles]);
  if (roles.length === 0) return null;

  const selected = roles.find((r) => r.postingId === postingId);
  const selectedOption = options.find((o) => o.value === selected?.postingId);

  return (
    <div className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">Add to an existing role</span>
      <Autocomplete
        aria-label="Add to an existing role"
        options={options}
        value={selectedOption}
        onValueChange={(option) => {
          const role = roles.find((r) => r.postingId === option?.value) ?? null;
          // Emptying the field is how "New role" is chosen — but only undo a pick that was made here,
          // never a posting searched for below.
          if (role || selected) onPick(role);
        }}
        placeholder="Search a role, or leave empty for a new role"
        shouldFilter
        openOnFocus
      />
      <span className="mt-1 block text-xs text-gray-500">
        {selected
          ? selected.hasActive
            ? `This link joins ${selected.name} in the overview.`
            : `${selected.name} has no open links, so it only shows in the overview with “Show closed” ticked.`
          : 'Pick a role to reuse its posting, so every stage stays grouped together. Otherwise search the posting below.'}
      </span>
    </div>
  );
}
